import {shallowEqual} from 'react-redux';
import {assert} from 'assert-ts';
import type {Schema} from 'schemaDefinition/types';
import type {CodeListMap, LocalCodeListId} from 'services/codelists/types';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition/functions';
import {AllLocalCodeListIds} from 'schemaDefinition/types/linkTypes';
import {selectCodelistFromCache} from 'services/codelists/codelistSelectors';
import {selectSettingsPreferredLocale} from 'services/settings';
import {useAppSelector} from 'store/hooks';
import {getLinkedRoleCodelist} from 'schemas/links/functions';
import {useSchemaListCodelistIds} from './useSchemaListCodelistIds';

/**
 * Assumes needed codelists have been loaded
 */
export const useSchemasCodelists = (schemas: Schema[]): CodeListMap => {
  const language = useAppSelector(selectSettingsPreferredLocale);
  const localization = useLocalization();
  const codelistIds = useSchemaListCodelistIds(schemas);

  return useAppSelector(state => {
    return codelistIds.reduce<CodeListMap>((acc, id) => {
      if (AllLocalCodeListIds.includes(id as LocalCodeListId)) {
        return acc;
      }

      if (isLinkedRoleCodeListId(id)) {
        acc[id] = getLinkedRoleCodelist(id, localization);
        return acc;
      }

      acc[id] = assert(
        selectCodelistFromCache(id, language, state),
        `useSchemaCodelists: missing codelist for id: ${id}`,
        {id},
      );
      return acc;
    }, {} as CodeListMap);
  }, shallowEqual);
};
